/* PTI Certified Light Therapist directory: search, filters, sorting and "Show more".
   Built as a custom element so theme-editor section reloads create a fresh instance
   instead of stacking event listeners. */
(() => {
  if (customElements.get('pti-clt-directory')) return;

  const normalize = (value) =>
    (value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLocaleLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  const split = (value) => (value ? value.split('|').filter(Boolean) : []);
  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

  class PtiCltDirectory extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const $ = (selector) => this.querySelector(selector);
      this.pageSize = Math.max(1, parseInt(this.dataset.pageSize, 10) || 12);
      this.limit = this.pageSize;
      this.form = $('[data-pti-clt-form]');
      this.grid = $('[data-pti-clt-grid]');
      if (!this.form || !this.grid) return;

      this.search = $('[data-pti-clt-search]');
      this.country = $('[data-pti-clt-country]');
      this.region = $('[data-pti-clt-region]');
      this.sort = $('[data-pti-clt-sort]');
      this.count = $('[data-pti-clt-count]');
      this.empty = $('[data-pti-clt-empty]');
      this.more = $('[data-pti-clt-more]');
      this.toggle = $('[data-pti-clt-toggle]');
      this.activeCount = $('[data-pti-clt-active]');
      this.inline = $('[data-pti-clt-inline]');
      this.drawer = $('[data-pti-clt-drawer]');
      this.drawerSlot = $('[data-pti-clt-drawer-slot]');
      this.apply = $('[data-pti-clt-apply]');
      this.mobile = window.matchMedia('(max-width: 959px)');
      if (this.drawer) {
        // Theme panels only appear above the theme overlay when they sit beside the
        // theme's own panels (such as #cart), so move the drawer there and bring the
        // section's colour settings with it.
        this.drawer.classList.add('pti-clt');
        this.drawer.setAttribute('style', this.getAttribute('style') || '');
        const themePanel = document.querySelector('.m6pn#cart, .m6pn:not(.pti-clt-drawer)');
        (themePanel ? themePanel.parentElement : document.getElementById('root') || document.body).append(this.drawer);
      }
      this.services = Array.from(this.querySelectorAll('[data-pti-clt-service]'));
      this.locations = Array.from(this.querySelectorAll('[data-pti-clt-location]'));
      this.resets = Array.from(this.querySelectorAll('[data-pti-clt-reset]'));

      this.cards = Array.from(this.grid.querySelectorAll('[data-pti-clt-card]'), (element) => ({
        element,
        name: element.dataset.name || '',
        last: element.dataset.last || element.dataset.name || '',
        business: element.dataset.business || '',
        country: element.dataset.country || '',
        region: element.dataset.region || '',
        services: split(element.dataset.services),
        locations: split(element.dataset.locations),
        text: normalize(element.dataset.search),
      }));
      this.regionOptions = Array.from(this.region.options)
        .slice(1)
        .map((option) => ({ value: option.value, country: option.dataset.country || '' }));

      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.search.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.update(), 200);
      });
      this.country.addEventListener('change', () => {
        this.updateRegions();
        this.update();
      });
      [this.region, this.sort, ...this.services, ...this.locations].forEach((control) =>
        control.addEventListener('change', () => this.update())
      );
      this.resets.forEach((button) => button.addEventListener('click', () => this.clear(button)));
      this.more.addEventListener('click', () => this.showMore());
      if (this.apply) this.apply.addEventListener('click', () => this.closeDrawer());
      this.onBreakpoint = () => this.placeFilters();
      this.mobile.addEventListener('change', this.onBreakpoint);
      // The theme re-applies tabindex="-1" to every panel shortly after opening one,
      // so keep this drawer's own controls reachable while it is open.
      if (this.drawer) {
        this.drawerObserver = new MutationObserver(() => this.syncDrawerFocus());
        this.drawerObserver.observe(this.drawer, { subtree: true, attributes: true, attributeFilter: ['class', 'tabindex'] });
      }

      this.form.hidden = false;
      if (this.toggle) this.toggle.hidden = false;
      this.querySelector('[data-pti-clt-sort-wrap]').hidden = false;
      this.classList.add('is-enhanced');
      this.placeFilters();
      this.updateRegions();
      this.render();
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      if (this.mobile && this.onBreakpoint) this.mobile.removeEventListener('change', this.onBreakpoint);
      if (this.drawerObserver) this.drawerObserver.disconnect();
      // The drawer lives outside this element; remove it unless the element was only moved.
      queueMicrotask(() => {
        if (!this.isConnected && this.drawer) this.drawer.remove();
      });
    }

    syncDrawerFocus() {
      const open = this.drawer.classList.contains('toggle');
      this.drawer.querySelectorAll('input, select, button, a').forEach((el) => {
        if (open && el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');
        if (!open && el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
      });
    }

    // One filter form: in the theme's slide-in panel on mobile, in the sidebar on desktop.
    placeFilters() {
      if (!this.drawer || !this.drawerSlot || !this.inline) return;
      const controls = this.form.querySelectorAll('input, select, button');
      if (this.mobile.matches) {
        if (this.form.parentElement !== this.drawerSlot) this.drawerSlot.append(this.form);
        // Keep hidden controls out of the tab order until the theme opens the panel.
        if (!this.drawer.classList.contains('toggle')) controls.forEach((el) => el.setAttribute('tabindex', '-1'));
      } else {
        if (this.drawer.classList.contains('toggle')) this.closeDrawer();
        if (this.form.parentElement !== this.inline) this.inline.append(this.form);
        controls.forEach((el) => el.removeAttribute('tabindex'));
      }
    }

    closeDrawer() {
      if (typeof window.hidePanels === 'function') {
        window.hidePanels();
      } else {
        const close = this.drawer.querySelector('.m6pn-close');
        if (close) close.click();
      }
    }

    updateRegions() {
      const country = this.country.value;
      const current = this.region.value;
      const seen = new Set();
      const options = this.regionOptions.filter((option) => {
        if (country && option.country !== country) return false;
        if (seen.has(option.value)) return false;
        seen.add(option.value);
        return true;
      });
      options.sort((a, b) => collator.compare(a.value, b.value));

      this.region.length = 1;
      options.forEach((option) => this.region.add(new Option(option.value, option.value)));
      this.region.value = options.some((option) => option.value === current) ? current : '';
      this.region.disabled = options.length === 0;
    }

    state() {
      return {
        text: normalize(this.search.value),
        country: this.country.value,
        region: this.region.value,
        services: this.services.filter((box) => box.checked).map((box) => box.value),
        locations: this.locations.filter((box) => box.checked).map((box) => box.value),
      };
    }

    filtered(state) {
      return (
        state.text ||
        state.country ||
        state.region ||
        state.services.length > 0 ||
        state.locations.length > 0
      );
    }

    matches(card, state) {
      if (state.text && !card.text.includes(state.text)) return false;
      if (state.country && card.country !== state.country) return false;
      if (state.region && card.region !== state.region) return false;
      // Within a group any selected option matches; groups combine.
      if (state.services.length && !state.services.some((value) => card.services.includes(value))) return false;
      if (state.locations.length && !state.locations.some((value) => card.locations.includes(value))) return false;
      return true;
    }

    compare(a, b) {
      const [key, direction] = (this.sort.value || 'last-asc').split('-');
      const order = direction === 'desc' ? -1 : 1;
      if (key === 'business') {
        // Listings without a business name go last in either direction.
        if (!a.business !== !b.business) return a.business ? -1 : 1;
        const result = collator.compare(a.business, b.business);
        if (result) return result * order;
      }
      return (collator.compare(a.last, b.last) || collator.compare(a.name, b.name)) * (key === 'last' ? order : 1);
    }

    update() {
      this.limit = this.pageSize;
      this.render();
    }

    render() {
      const state = this.state();
      const matched = this.cards.filter((card) => this.matches(card, state)).sort((a, b) => this.compare(a, b));
      const matchedSet = new Set(matched);
      const fragment = document.createDocumentFragment();
      matched.forEach((card) => fragment.append(card.element));
      this.cards.filter((card) => !matchedSet.has(card)).forEach((card) => fragment.append(card.element));
      this.grid.append(fragment);

      this.cards.forEach((card) => {
        card.element.hidden = true;
      });
      matched.forEach((card, index) => {
        card.element.hidden = index >= this.limit;
      });

      const shown = Math.min(this.limit, matched.length);
      const remaining = matched.length - shown;
      const total = this.cards.length;
      const filtered = this.filtered(state);

      this.grid.hidden = matched.length === 0;
      this.empty.hidden = matched.length > 0;
      this.more.hidden = remaining <= 0;
      this.more.textContent = `Show more therapists (${remaining} more)`;
      this.resets.forEach((button) => {
        if (button.closest('[data-pti-clt-form]')) button.disabled = !filtered;
      });

      const noun = (count) => (count === 1 ? 'therapist' : 'therapists');
      const active =
        (state.text ? 1 : 0) + (state.country ? 1 : 0) + (state.region ? 1 : 0) +
        state.services.length + state.locations.length;
      if (this.activeCount) {
        this.activeCount.hidden = active === 0;
        this.activeCount.textContent = String(active);
      }
      if (this.toggle) this.toggle.setAttribute('aria-label', active ? `Filters, ${active} active` : 'Filters');
      if (this.apply) {
        this.apply.textContent = matched.length ? `Show ${matched.length} ${noun(matched.length)}` : 'No matches: change your filters';
      }
      if (!matched.length) {
        this.count.textContent = 'No therapists match these filters';
      } else if (filtered) {
        this.count.textContent = `${matched.length} of ${total} ${noun(total)} match` +
          (remaining > 0 ? `, showing ${shown}` : '');
      } else {
        this.count.textContent = remaining > 0 ? `Showing ${shown} of ${total} ${noun(total)}` : `${total} ${noun(total)}`;
      }
      this.matched = matched;
    }

    showMore() {
      const firstNew = this.limit;
      this.limit += this.pageSize;
      this.render();
      const card = this.matched[firstNew];
      if (card) card.element.querySelector('.pti-clt-card__link').focus();
    }

    clear(button) {
      this.form.reset();
      this.updateRegions();
      this.update();
      if (!button.closest('[data-pti-clt-form]')) (this.mobile.matches && this.toggle ? this.toggle : this.search).focus();
    }
  }

  customElements.define('pti-clt-directory', PtiCltDirectory);
})();
